import { useEffect, useState, useRef, useCallback } from "react";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { toast } from "sonner";
import CanonicalUploader from "@/components/documents/CanonicalUploader";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const REQ_TONE = {
  missing:         ["bg-rose-50 text-rose-600 border-rose-200",   "Needed"],
  requested:       ["bg-amber-50 text-amber-700 border-amber-200", "Requested"],
  needs_revision:  ["bg-orange-50 text-orange-700 border-orange-200", "Needs Revision"],
  uploaded:        ["bg-blue-50 text-blue-600 border-blue-200",   "Uploaded"],
  awaiting_review: ["bg-blue-50 text-blue-600 border-blue-200",   "In Review"],
  approved:        ["bg-emerald-50 text-emerald-600 border-emerald-200", "Received"],
  not_available:   ["bg-slate-100 text-slate-600 border-slate-300", "Not Available"],
};

function Badge({ status }) {
  const [cls, label] = REQ_TONE[status] || ["bg-slate-100 text-slate-500 border-slate-200", status];
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>;
}

const PROC_TONE = {
  uploaded:                ["bg-slate-100 text-slate-500", "Queued",          "Clock"],
  security_scanning:       ["bg-indigo-50 text-indigo-600","Checking",        "ShieldCheck"],
  processing_preview:      ["bg-blue-50 text-blue-600",   "Preparing",        "Loader2"],
  extracting_text:         ["bg-blue-50 text-blue-600",   "Processing",       "Loader2"],
  partially_ready:         ["bg-emerald-50 text-emerald-600","Available",      "CheckCircle2"],
  quarantined:             ["bg-rose-50 text-rose-600",   "Security review",  "ShieldAlert"],
  pending_security_review: ["bg-rose-50 text-rose-600",   "Security review",  "ShieldAlert"],
  processing_failed:       ["bg-amber-50 text-amber-700", "Unable to process","AlertTriangle"],
};
function ProcBadge({ status }) {
  if (!status || status === "ready") return null;
  const [cls, label, icon] = PROC_TONE[status] || ["bg-slate-100 text-slate-500", status, "Circle"];
  const Ic = Icons[icon] || Icons.Circle;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full ${cls}`} data-testid="portal-proc-badge">
      <Ic className={`h-3 w-3 ${icon === "Loader2" ? "animate-spin" : ""}`} />{label}
    </span>
  );
}

const BROWSE_MODES = [
  { key: "tax_year",   label: "Tax Year",   icon: "Calendar" },
  { key: "category",   label: "Category",   icon: "FolderOpen" },
  { key: "engagement", label: "Engagement", icon: "FileSignature" },
  { key: "all",        label: "All",        icon: "Files" },
];

const CAT_LABELS = {
  engagement_agreements: "Engagement Agreements",
  prior_tax_returns: "Tax Returns", income_documents: "Income Documents",
  w2: "W-2", "1099": "1099", k1: "K-1", banking: "Banking",
  financial_statements: "Financial Statements", investments: "Investments",
  identity: "Identity", fbar_support: "FBAR Support", fatca_support: "FATCA Support",
  rental_property: "Rental Property", crypto: "Crypto", thai_tax: "Thai Tax",
  thai_tin: "Thai TIN", irs_notices: "IRS Notices", legal_documents: "Legal Documents",
  engagement_letters: "Engagement Letters", correspondence: "Correspondence",
  organizer_support: "Organizer Support", supporting_documentation: "Supporting Documentation",
  q_and_a_response: "Q&A Responses", client_narrative: "Client Narratives",
  deliverables: "Deliverables", business_records: "Business Records", other: "Other",
  foreign_accounts: "Foreign Accounts",
};

function catLabel(key) {
  return CAT_LABELS[key] || (key || "Other").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function PortalDocuments() {
  const { can } = usePortalAuth();
  const [data, setData] = useState(null);
  const [browseMode, setBrowseMode] = useState("tax_year");
  const [openYears, setOpenYears] = useState({});
  const [uploading, setUploading] = useState(false);
  const reqFileRef = useRef(null);
  const [targetReq, setTargetReq] = useState(null);
  const [explainReq, setExplainReq] = useState(null);
  const [explainText, setExplainText] = useState("");
  const [explaining, setExplaining] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [reqUploader, setReqUploader] = useState(null);
  const [dup, setDup] = useState(null);
  const [clarifs, setClarifs] = useState([]);
  const [respondFor, setRespondFor] = useState(null);
  const [respondText, setRespondText] = useState("");
  const [viewer, setViewer] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const searchTimer = useRef(null);

  const load = useCallback(async (q) => {
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : "";
      const { data } = await portalApi.get(`/portal/documents${params}`);
      setData(data);
    } catch {}
    try { const { data } = await portalApi.get("/portal/clarifications"); setClarifs(data.clarifications || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  // Auto-expand the first year bucket when data arrives.
  useEffect(() => {
    if (!data?.groups?.by_tax_year?.sorted_keys?.length) return;
    const first = data.groups.by_tax_year.sorted_keys[0];
    setOpenYears((prev) => (Object.keys(prev).length ? prev : { [first]: true }));
  }, [data]);

  const TERMINAL = ["ready", "partially_ready", "quarantined", "pending_security_review", "processing_failed"];
  useEffect(() => {
    const anyProcessing = (data?.uploaded || []).some((d) => d.processing_status && !TERMINAL.includes(d.processing_status));
    if (!anyProcessing) return;
    const t = setTimeout(() => load(), 4000);
    return () => clearTimeout(t);
  }, [data, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const OFFICE = ["odt","doc","docx","rtf","xls","xlsx","ods","ppt","pptx","odp"];
  const IMG    = ["jpg","jpeg","png","webp","gif","bmp"];
  const openViewer = async (doc) => {
    const ext     = (doc.original_filename || "").split(".").pop().toLowerCase();
    const variant = OFFICE.includes(ext) ? "preview" : "original";
    const kind    = OFFICE.includes(ext) ? "pdf" : (ext === "pdf" ? "pdf" : (IMG.includes(ext) ? "image" : (["txt","csv","json","xml","md"].includes(ext) ? "text" : "other")));
    if (kind === "other") { toast.message("Preview unavailable — downloading original."); return download(doc); }
    setViewer({ loading: true, name: doc.original_filename, kind });
    try {
      const res = await portalApi.get(`/portal/documents/${doc.id}/content?variant=${variant}`, { responseType: "blob" });
      setViewer({ url: URL.createObjectURL(res.data), kind, name: doc.original_filename });
    } catch (e) {
      setViewer(null);
      const status = e?.response?.status;
      if (status === 423) {
        toast.error("This file is being reviewed for security. Please check back shortly.");
      } else if (status === 402) {
        toast.error("An outstanding balance is on this engagement. Please contact your advisor to resolve it before accessing this document.");
      } else if (status === 409) {
        toast.error("This document is no longer available. Please contact your advisor for assistance.");
      } else if (status === 503) {
        toast.error("This document is temporarily unavailable. Please try again in a moment.");
      } else {
        toast.error("Could not open this file. Please try again or contact support.");
      }
    }
  };

  const respond = async (cid) => {
    if (!respondText.trim()) { toast.error("Please enter a response."); return; }
    try { await portalApi.post(`/portal/clarifications/${cid}/respond`, { message: respondText.trim() }); toast.success("Thanks — your advisor has been notified."); setRespondFor(null); setRespondText(""); load(); }
    catch { toast.error("Could not send your response."); }
  };

  const portalUploadFn = useCallback((requestId) => (file, { signal, asVersion, note, onProgress }) => {
    const form = new FormData();
    form.append("file", file);
    if (requestId) form.append("request_id", requestId);
    if (asVersion) form.append("as_version", "true");
    if (note) form.append("version_note", note);
    return portalApi.post("/portal/documents/upload", form, {
      headers: { "Content-Type": "multipart/form-data" }, signal,
      onUploadProgress: (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
    }).then((r) => r.data).catch((err) => {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "duplicate_document") {
        const e2 = new Error("This file was already uploaded — keep as a new version?");
        e2.duplicate = detail.existing; throw e2;
      }
      throw new Error("Upload failed. Please try again.");
    });
  }, []);

  const upload = async (file, requestId = null, opts = {}) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    if (requestId) form.append("request_id", requestId);
    if (opts.asVersion) form.append("as_version", "true");
    try {
      const { data } = await portalApi.post("/portal/documents/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.matched_request) toast.success(`${file.name} matched "${data.matched_request.title}" — sent for review.`);
      else toast.success(`${file.name} uploaded — your advisor has been notified.`);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "duplicate_document") setDup({ existing: detail.existing, file, requestId });
      else toast.error("Upload failed. Please try again.");
    }
    finally { setUploading(false); if (reqFileRef.current) reqFileRef.current.value = ""; }
  };

  const submitExplain = async (reqId) => {
    if (!explainText.trim()) { toast.error("Please add a brief explanation."); return; }
    setExplaining(true);
    try {
      await portalApi.post(`/portal/documents/requests/${reqId}/explain`, { explanation: explainText.trim() });
      toast.success("Thanks — your advisor has been notified.");
      setExplainReq(null); setExplainText(""); load();
    } catch { toast.error("Could not submit. Please try again."); }
    finally { setExplaining(false); }
  };

  const download = async (doc) => {
    try {
      const res = await portalApi.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.original_filename || "document"; a.click(); URL.revokeObjectURL(url);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        toast.error("An outstanding balance is on this engagement. Please contact your advisor to resolve it before downloading this document.");
      } else if (status === 409) {
        toast.error("This document is no longer available. Please contact your advisor for assistance.");
      } else if (status === 503) {
        toast.error("This document is temporarily unavailable. Please try again in a moment.");
      } else {
        toast.error("Could not download this file.");
      }
    }
  };

  if (!data) return <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;
  const canUpload = can("upload_documents");

  // ---- Shared card renderers -------------------------------------------------------
  const RequestRow = ({ r }) => {
    const canRespond = canUpload && ["missing", "requested", "needs_revision"].includes(r.request_status);
    return (
      <div key={r.id} className="px-4 py-3" data-testid={`portal-req-row-${r.id}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icons.FileText className="h-4 w-4 text-slate-400" /></div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#171B2E] truncate">{r.title || r.document_type || "Document"}</div>
              {r.description && <div className="text-xs text-slate-400 truncate">{r.description}</div>}
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Badge status={r.request_status} />
            {canRespond && <>
              <button onClick={() => setReqUploader(reqUploader === r.id ? null : r.id)} data-testid={`portal-req-upload-${r.id}`}
                className="inline-flex items-center gap-1 text-xs bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-medium rounded-md px-2.5 py-1">
                <Icons.Upload className="h-3 w-3" />{r.request_status === "needs_revision" ? "Re-upload" : "Upload"}
              </button>
              <button onClick={() => { setExplainReq(explainReq === r.id ? null : r.id); setExplainText(""); }} data-testid={`portal-req-explain-${r.id}`}
                className="inline-flex items-center gap-1 text-xs border border-slate-200 hover:bg-slate-50 text-slate-600 font-medium rounded-md px-2.5 py-1">
                <Icons.HelpCircle className="h-3 w-3" />I don't have this
              </button>
            </>}
          </div>
        </div>
        {canRespond && reqUploader === r.id && (
          <div className="mt-2 ml-11" data-testid={`portal-req-uploader-${r.id}`}>
            <CanonicalUploader compact uploadFn={portalUploadFn(r.id)} allowSeparate={false} onFileUploaded={() => load()} testId={`portal-req-uploader-${r.id}`} />
          </div>
        )}
        {r.request_status === "not_available" && r.client_explanation && (
          <div className="mt-2 ml-11 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-md px-3 py-2" data-testid={`portal-req-explanation-${r.id}`}>
            <span className="font-medium text-slate-600">Your note:</span> {r.client_explanation}
          </div>
        )}
        {explainReq === r.id && (
          <div className="mt-2 ml-11 space-y-2" data-testid={`portal-req-explain-form-${r.id}`}>
            <textarea value={explainText} onChange={(e) => setExplainText(e.target.value)} rows={2}
              placeholder="Let us know why you can't provide this…"
              className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40" data-testid={`portal-req-explain-input-${r.id}`} />
            <div className="flex items-center gap-2">
              <button onClick={() => submitExplain(r.id)} disabled={explaining} data-testid={`portal-req-explain-submit-${r.id}`}
                className="inline-flex items-center gap-1 text-xs bg-[#171B2E] hover:bg-[#2a3050] text-white font-medium rounded-md px-3 py-1.5 disabled:opacity-60">
                {explaining ? <Icons.Loader2 className="h-3 w-3 animate-spin" /> : <Icons.Send className="h-3 w-3" />}Submit explanation
              </button>
              <button onClick={() => { setExplainReq(null); setExplainText(""); }} className="text-xs text-slate-400 hover:text-slate-600 px-2 py-1.5">Cancel</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const FileRow = ({ d }) => (
    <div key={d.id} className="px-4 py-3 flex items-center justify-between gap-4" data-testid={`portal-file-row-${d.id}`}>
      <button onClick={() => openViewer(d)} data-testid={`portal-view-${d.id}`} className="flex items-center gap-3 min-w-0 text-left group">
        <div className="h-8 w-8 rounded-lg bg-[#F5A623]/15 flex items-center justify-center shrink-0"><Icons.File className="h-4 w-4 text-[#d98b12]" /></div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#171B2E] truncate group-hover:underline">
            {d.display_filename || d.original_filename}
            {d.version > 1 && <span className="ml-1 text-[10px] text-slate-400">v{d.version}</span>}
          </div>
          <div className="text-xs text-slate-400 flex items-center gap-1.5">{fmtDate(d.created_at)}{d.upload_source === "portal_upload" ? " · uploaded by you" : " · shared by your team"}<ProcBadge status={d.processing_status} /></div>
        </div>
      </button>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={() => openViewer(d)} className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-[#171B2E] rounded-md px-2.5 py-1.5" data-testid={`portal-view-btn-${d.id}`}><Icons.Eye className="h-3.5 w-3.5" />View</button>
        <button onClick={() => download(d)} data-testid={`portal-download-${d.id}`} className="p-2 rounded-lg text-slate-500 hover:text-[#171B2E] hover:bg-slate-100"><Icons.Download className="h-4 w-4" /></button>
      </div>
    </div>
  );

  // ---- Tax Year view -------------------------------------------------------
  const TaxYearView = () => {
    const { buckets = {}, sorted_keys = [] } = data?.groups?.by_tax_year || {};
    if (!sorted_keys.length) return <EmptyState />;
    return (
      <div className="space-y-3" data-testid="portal-browse-tax-year">
        {sorted_keys.map((year) => {
          const bucket = buckets[year] || { requests: [], documents: [] };
          const isOpen = !!openYears[year];
          const pendingReqs = bucket.requests.filter((r) => ["missing","requested","needs_revision"].includes(r.request_status)).length;
          const yearLabel = year === "not_tax_year_specific" ? "Not Tax-Year Specific" : year;
          return (
            <div key={year} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setOpenYears((p) => ({ ...p, [year]: !p[year] }))}
                className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-slate-50 transition-colors"
                data-testid={`portal-year-accordion-${year}`}
              >
                <Icons.Calendar className="h-5 w-5 text-[#F5A623] shrink-0" />
                <span className="font-heading font-semibold text-[#171B2E]">{yearLabel}</span>
                <div className="ml-auto flex items-center gap-2">
                  {pendingReqs > 0 && (
                    <span className="text-[11px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`portal-year-pending-${year}`}>
                      {pendingReqs} needed
                    </span>
                  )}
                  <span className="text-xs text-slate-400">{bucket.requests.length + bucket.documents.length} items</span>
                  <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100">
                  {bucket.requests.length > 0 && (
                    <>
                      <div className="px-5 py-2 bg-slate-50 border-b border-slate-100">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Requested</span>
                      </div>
                      <div className="divide-y divide-slate-50">
                        {bucket.requests.map((r) => <RequestRow key={r.id} r={r} />)}
                      </div>
                    </>
                  )}
                  {bucket.documents.length > 0 && (
                    <>
                      <div className="px-5 py-2 bg-slate-50 border-b border-slate-100">
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Files</span>
                      </div>
                      <div className="divide-y divide-slate-50">
                        {bucket.documents.map((d) => <FileRow key={d.id} d={d} />)}
                      </div>
                    </>
                  )}
                  {!bucket.requests.length && !bucket.documents.length && (
                    <div className="p-6 text-center text-sm text-slate-400">No items for this period.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ---- Category view -------------------------------------------------------
  const CategoryView = () => {
    const { buckets = {}, sorted_keys = [] } = data?.groups?.by_category || {};
    if (!sorted_keys.length) return <EmptyState />;
    return (
      <div className="space-y-3" data-testid="portal-browse-category">
        {sorted_keys.map((cat) => {
          const bucket = buckets[cat] || { requests: [], documents: [] };
          const isOpen = !!openYears[`cat_${cat}`];
          return (
            <div key={cat} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button onClick={() => setOpenYears((p) => ({ ...p, [`cat_${cat}`]: !p[`cat_${cat}`] }))}
                className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-slate-50" data-testid={`portal-cat-accordion-${cat}`}>
                <Icons.FolderOpen className="h-5 w-5 text-[#F5A623] shrink-0" />
                <span className="font-heading font-semibold text-[#171B2E]">{catLabel(cat)}</span>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-slate-400">{bucket.requests.length + bucket.documents.length} items</span>
                  <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 divide-y divide-slate-50">
                  {bucket.requests.map((r) => <RequestRow key={r.id} r={r} />)}
                  {bucket.documents.map((d) => <FileRow key={d.id} d={d} />)}
                  {!bucket.requests.length && !bucket.documents.length && (
                    <div className="p-6 text-center text-sm text-slate-400">No items.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ---- Engagement view -----------------------------------------------------
  const EngagementView = () => {
    const { buckets = {}, sorted_keys = [] } = data?.groups?.by_engagement || {};
    if (!sorted_keys.length) return <EmptyState />;
    return (
      <div className="space-y-3" data-testid="portal-browse-engagement">
        {sorted_keys.map((eng) => {
          const bucket = buckets[eng] || { requests: [], documents: [] };
          const isOpen = !!openYears[`eng_${eng}`];
          const label = eng === "no_engagement" ? "Not Linked to an Engagement" : (bucket.label || eng);
          return (
            <div key={eng} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button onClick={() => setOpenYears((p) => ({ ...p, [`eng_${eng}`]: !p[`eng_${eng}`] }))}
                className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-slate-50" data-testid={`portal-eng-accordion-${eng}`}>
                <Icons.FileSignature className="h-5 w-5 text-[#F5A623] shrink-0" />
                <span className="font-heading font-semibold text-[#171B2E] truncate">{label}</span>
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-xs text-slate-400">{bucket.requests.length + bucket.documents.length} items</span>
                  <Icons.ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-slate-100 divide-y divide-slate-50">
                  {bucket.requests.map((r) => <RequestRow key={r.id} r={r} />)}
                  {bucket.documents.map((d) => <FileRow key={d.id} d={d} />)}
                  {!bucket.requests.length && !bucket.documents.length && (
                    <div className="p-6 text-center text-sm text-slate-400">No items.</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ---- All Documents view (legacy flat layout) -----------------------------
  const AllView = () => (
    <div className="space-y-4" data-testid="portal-browse-all">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2"><Icons.ListChecks className="h-5 w-5 text-[#F5A623]" /><h2 className="font-heading font-semibold text-[#171B2E]">Requested Documents</h2></div>
          {data.progress && data.progress.total > 0 && (
            <div className="mt-3" data-testid="portal-docs-progress">
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-slate-500">Collection progress</span>
                <span className="font-semibold text-[#171B2E]">{data.progress.uploaded} of {data.progress.total} received{data.progress.outstanding > 0 ? ` · ${data.progress.outstanding} to go` : ""}</span>
              </div>
              <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-700 ease-out bg-emerald-500" style={{ width: `${data.progress.percent}%` }} />
              </div>
              {data.progress.outstanding > 0
                ? <p className="text-xs text-slate-500 mt-2">Upload the remaining items so we can keep your filing on track.</p>
                : <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><Icons.CheckCircle2 className="h-3.5 w-3.5" />All requested documents received — thank you!</p>}
            </div>
          )}
        </div>
        {(data.requests || []).length === 0
          ? <div className="p-8 text-center text-sm text-slate-400">No document requests yet.</div>
          : <div className="divide-y divide-slate-50" data-testid="portal-doc-requests">{(data.requests || []).map((r) => <RequestRow key={r.id} r={r} />)}</div>
        }
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2"><Icons.FolderOpen className="h-5 w-5 text-[#F5A623]" /><h2 className="font-heading font-semibold text-[#171B2E]">Your Files</h2></div>
        {(data.uploaded || []).length === 0
          ? <div className="p-8 text-center text-sm text-slate-400">No files yet.{canUpload ? " Upload your first document above." : ""}</div>
          : <div className="divide-y divide-slate-50" data-testid="portal-doc-files">{(data.uploaded || []).map((d) => <FileRow key={d.id} d={d} />)}</div>
        }
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center text-slate-400 text-sm">
      No documents yet. {canUpload ? "Upload your first document above." : ""}
    </div>
  );

  const VIEW_COMPONENTS = { tax_year: TaxYearView, category: CategoryView, engagement: EngagementView, all: AllView };
  const ActiveView = VIEW_COMPONENTS[browseMode] || AllView;

  return (
    <div className="space-y-6" data-testid="portal-documents">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#171B2E]">Documents</h1>
          <p className="text-slate-500 text-sm mt-1">Upload requested files and access your shared documents securely.</p>
        </div>
        {canUpload && (
          <button onClick={() => setShowUploader((s) => !s)} data-testid="portal-upload-btn"
            className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#e0951a] text-[#171B2E] font-semibold rounded-lg px-4 py-2.5 text-sm">
            <Icons.Upload className="h-4 w-4" /> Upload Document
          </button>
        )}
      </div>

      {/* Uploader */}
      {canUpload && showUploader && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="portal-upload-panel">
          <CanonicalUploader uploadFn={portalUploadFn(null)} allowSeparate={false} onFileUploaded={() => load()} testId="portal-uploader" />
        </div>
      )}

      {/* Clarifications */}
      {clarifs.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3" data-testid="portal-clarifications">
          <div className="flex items-center gap-2 text-amber-800 font-semibold"><Icons.MessageCircleQuestion className="h-5 w-5" />Your advisor needs a quick clarification</div>
          {clarifs.map((cl) => (
            <div key={cl.id} className="bg-white rounded-xl border border-amber-100 p-3" data-testid={`portal-clarification-${cl.id}`}>
              <div className="text-sm text-[#171B2E]">{cl.message}</div>
              {cl.requested_correction && <div className="text-xs text-slate-500 mt-1">Requested: {cl.requested_correction}</div>}
              {(cl.responses || []).map((r, i) => <div key={i} className="text-xs text-emerald-700 mt-1">You replied: {r.message}</div>)}
              {cl.status === "open" ? (
                respondFor === cl.id ? (
                  <div className="mt-2 space-y-2">
                    <textarea value={respondText} onChange={(e) => setRespondText(e.target.value)} rows={2} className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2" data-testid={`portal-clarif-input-${cl.id}`} placeholder="Type your response…" />
                    <div className="flex gap-2">
                      <button onClick={() => respond(cl.id)} data-testid={`portal-clarif-send-${cl.id}`} className="text-xs bg-[#171B2E] text-white rounded-md px-3 py-1.5">Send</button>
                      <button onClick={() => setRespondFor(null)} className="text-xs text-slate-500 px-2 py-1.5">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => { setRespondFor(cl.id); setRespondText(""); }} data-testid={`portal-clarif-respond-${cl.id}`} className="mt-2 text-xs bg-[#F5A623] text-[#171B2E] font-medium rounded-md px-3 py-1.5">Respond</button>
                )
              ) : <span className="mt-2 inline-block text-xs text-emerald-600">Answered</span>}
            </div>
          ))}
        </div>
      )}

      {/* Search bar */}
      <div className="relative" data-testid="portal-search-bar">
        <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          value={searchQ}
          onChange={(e) => {
            const v = e.target.value;
            setSearchQ(v);
            clearTimeout(searchTimer.current);
            searchTimer.current = setTimeout(() => load(v), 350);
          }}
          placeholder="Search your documents…"
          data-testid="portal-search-input"
          className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-[#171B2E] focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 shadow-sm"
        />
        {searchQ && (
          <button onClick={() => { setSearchQ(""); load(""); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="portal-search-clear">
            <Icons.X className="h-4 w-4" />
          </button>
        )}
      </div>
      {data?.search_active && (
        <p className="text-xs text-[#F5A623] -mt-2 ml-1" data-testid="portal-search-active">
          Showing results for "{data.search_q}"
        </p>
      )}

      {/* Browse mode selector */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="portal-browse-mode-selector">
        {BROWSE_MODES.map(({ key, label, icon }) => {
          const Ic = Icons[icon] || Icons.Files;
          const active = browseMode === key;
          return (
            <button key={key} onClick={() => setBrowseMode(key)} data-testid={`portal-browse-${key}`}
              className={`inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-xl border transition-colors ${active ? "bg-[#171B2E] text-white border-[#171B2E]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
              <Ic className="h-3.5 w-3.5" />{label}
            </button>
          );
        })}
      </div>

      {/* Active view */}
      <ActiveView />

      {/* Duplicate prompt */}
      {dup && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="portal-duplicate-prompt">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4">
            <div className="flex items-center gap-2 text-[#171B2E] font-heading font-semibold"><Icons.Copy className="h-5 w-5 text-[#F5A623]" />This file was already uploaded</div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              Existing: <strong>{dup.existing.filename}</strong> · v{dup.existing.version} · {fmtDate(dup.existing.uploaded_at)}
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => { openViewer({ id: dup.existing.id, original_filename: dup.existing.filename }); setDup(null); }} data-testid="portal-dup-view" className="text-sm px-3 py-2 rounded-lg border border-slate-200 inline-flex items-center gap-1.5"><Icons.Eye className="h-4 w-4" />View Existing</button>
              <button onClick={() => { const d = dup; setDup(null); upload(d.file, d.requestId, { asVersion: true }); }} data-testid="portal-dup-newversion" className="text-sm px-3 py-2 rounded-lg bg-[#F5A623] text-[#171B2E] font-semibold inline-flex items-center gap-1.5"><Icons.GitBranch className="h-4 w-4" />Keep as New Version</button>
              <button onClick={() => setDup(null)} data-testid="portal-dup-cancel" className="text-sm px-3 py-2 rounded-lg border border-slate-200">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* In-app viewer */}
      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => { if (viewer.url) URL.revokeObjectURL(viewer.url); setViewer(null); }} data-testid="portal-viewer-modal">
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Icons.FileText className="h-4 w-4 text-slate-400" /><span className="text-sm font-medium truncate">{viewer.name}</span>
              <button onClick={() => { if (viewer.url) URL.revokeObjectURL(viewer.url); setViewer(null); }} className="ml-auto p-1.5 rounded hover:bg-slate-100" data-testid="portal-viewer-close"><Icons.X className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 bg-slate-100 overflow-auto flex items-center justify-center">
              {viewer.loading && <Icons.Loader2 className="h-6 w-6 animate-spin text-slate-400" />}
              {viewer.url && viewer.kind === "pdf" && <iframe title={viewer.name} src={viewer.url} className="w-full h-full" data-testid="portal-viewer-pdf" />}
              {viewer.url && viewer.kind === "image" && <img src={viewer.url} alt={viewer.name} className="max-w-full max-h-full object-contain" data-testid="portal-viewer-image" />}
              {viewer.url && viewer.kind === "text" && <iframe title={viewer.name} src={viewer.url} className="w-full h-full bg-white" data-testid="portal-viewer-text" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
